import React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import type { RootState } from 'reducers/types';
import {
  amountFrom as getAmountFrom,
  amountTo as getAmountTo,
  getCurrency,
  getRecurringInterval,
} from 'selectors';
import type { I18n } from 'utils';
import { useTranslations } from 'utils/translations/useTranslations';

import { Installment as InstallmentBuilder } from 'domain/recurringReceivables/builders/Installment/Installment';

type RenderProps = {
  amountFrom: number;
  amountTo: number;
  fee: number;
  currencyFrom: string;
  currencyTo: string;
  ends?: string;
  interval: number;
  starts?: string;
  i18n: I18n;
};

type Props = ConnectedProps<typeof connector> & {
  render?: (props: RenderProps) => React.ReactElement<RenderProps>;
};

function Wrapper({ amountFrom, amountTo, interval, render }: Props) {
  const { currency: currencyFrom } = amountFrom;
  const { currency: currencyTo } = amountTo;

  const i18n = useTranslations();

  const installment = new InstallmentBuilder({
    amount: amountFrom.value,
    interval,
    subunitToUnit: currencyFrom.subunitToUnit,
    unitsToRound: currencyFrom.unitsToRound,
  });

  return render
    ? render({
        amountFrom: amountFrom.value as number,
        amountTo: amountTo.value,
        fee: installment.fee,
        currencyFrom: currencyFrom.code,
        currencyTo: currencyTo.code,
        ends: installment.ends?.toLocaleDateString(),
        i18n,
        interval,
        starts: installment.starts?.toLocaleDateString(),
      })
    : null;
}

Wrapper.displayName = 'RecurringReceivableWrapper';

const mapStateToProps = (state: RootState) => {
  const amountFrom = getAmountFrom(state);
  const currencyFrom = getCurrency(state, amountFrom.currency as string);
  const amountTo = getAmountTo(state);
  const currencyTo = getCurrency(state, amountTo.currency as string);
  const interval = getRecurringInterval(state);

  return {
    amountFrom: { ...amountFrom, currency: currencyFrom },
    amountTo: { ...amountTo, currency: currencyTo },
    interval,
  };
};

const connector = connect(mapStateToProps);
const RecurringReceivable = connector(Wrapper);

export { RecurringReceivable };
